// Brand Preview — three candidate names rendered in the live TRAQO design
// language (cream #F7F5F0 + forest #0A1F1C + electric lime #E8FF3A,
// Bricolage Grotesque). Each candidate ships:
//
//   • A bespoke logo mark (rounded-square badge, monochrome SVG glyph)
//   • Wordmark in the platform display font
//   • 3 tagline options
//   • A hero/landing card mock so you can see the brand in context
//   • A favicon-scale + horizontal-rail rendering at the bottom
//
// Pick a winner; we then swap it across the codebase (Logo.jsx, Landing,
// PortalShell, favicon, manifest, emails, PDF footer).
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Check, Zap, Layers } from "lucide-react";

const CREAM   = "#F7F5F0";
const FOREST  = "#0A1F1C";
const LIME    = "#E8FF3A";

// ── Logo marks (monochrome SVGs — accent on forest, swap colors for inverse) ──
const VerraMark = ({ size = 28, bg = FOREST, fg = LIME }) => (
  <div
    className="rounded-md grid place-items-center"
    style={{ width: size, height: size, background: bg }}
  >
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none">
      {/* "V" with an embedded check tick — verification + truth */}
      <path
        d="M3 5 L11.5 19 L21 5"
        stroke={fg}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="11.5" cy="19" r="1.6" fill={fg} />
    </svg>
  </div>
);

const TruvaMark = ({ size = 28, bg = FOREST, fg = LIME }) => (
  <div
    className="rounded-md grid place-items-center"
    style={{ width: size, height: size, background: bg }}
  >
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
      {/* Two interlocking arcs — buyer + seller, on one platform */}
      <path
        d="M8 6 A6 6 0 1 0 8 18"
        stroke={fg}
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M16 6 A6 6 0 1 1 16 18"
        stroke={fg}
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const VerisMark = ({ size = 28, bg = FOREST, fg = LIME }) => (
  <div
    className="rounded-md grid place-items-center"
    style={{ width: size, height: size, background: bg }}
  >
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
      {/* Three stacked bars — the wholesale infrastructure layer */}
      <rect x="4"  y="6"  width="16" height="2.4" rx="1.2" fill={fg} />
      <rect x="4"  y="11" width="12" height="2.4" rx="1.2" fill={fg} />
      <rect x="4"  y="16" width="8"  height="2.4" rx="1.2" fill={fg} />
    </svg>
  </div>
);

// ── Candidate config ─────────────────────────────────────────────────────
const CANDIDATES = [
  {
    name: "Verra",
    Mark: VerraMark,
    pronunciation: "vehr-uh",
    etymology: "Latin verus — true / verifiable",
    angle: "Trust + verification. The Stripe-shaped bet.",
    taglines: [
      "Wholesale, made true.",
      "The B2B operating system.",
      "Verified wholesale. Instantly settled.",
    ],
    hero: {
      eyebrow: "B2B wholesale OS",
      title: "Buy now. Pay in 60.\nVerified by Verra.",
      sub: "One platform for catalog, payments, credit, insurance & shipping. No more 12 logins.",
    },
  },
  {
    name: "Truva",
    Mark: TruvaMark,
    pronunciation: "troo-vah",
    etymology: "Coined — true + Slavic flow suffix",
    angle: "Two parties, one platform. Most defensible TM.",
    taglines: [
      "Where wholesale gets paid.",
      "Wholesale, in one click.",
      "Two sides. One platform. Truva.",
    ],
    hero: {
      eyebrow: "The wholesale layer",
      title: "Every buyer.\nEvery supplier.\nOne click.",
      sub: "Truva is the connective tissue underneath modern wholesale — credit decisioned, payments cleared, insurance attached.",
    },
  },
  {
    name: "Veris",
    Mark: VerisMark,
    pronunciation: "veh-ris",
    etymology: "Latin veris — of truth, of spring",
    angle: "Infrastructure-coded. Layer / stack feel.",
    taglines: [
      "The wholesale layer.",
      "True B2B infrastructure.",
      "Veris — the rails wholesale runs on.",
    ],
    hero: {
      eyebrow: "Infrastructure for wholesale",
      title: "The rails every\nwholesale brand\nshould be on.",
      sub: "Veris ships the layer — APIs for credit, capital, claims and shipping — so you can ship product, not paperwork.",
    },
  },
];

// ── In-context hero card (mock landing) ──────────────────────────────────
function HeroMock({ candidate }) {
  const { Mark, name, hero } = candidate;
  return (
    <div className="rounded-2xl border border-foreground/10 bg-background overflow-hidden">
      {/* Faux nav */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-foreground/10 bg-background/80">
        <div className="flex items-center gap-2.5">
          <Mark size={26} />
          <span className="display font-semibold text-[16px] tracking-tight">{name}</span>
        </div>
        <div className="hidden sm:flex items-center gap-5 text-[12.5px] text-foreground/65">
          <span>Platform</span><span>Products</span><span>Pricing</span>
        </div>
        <button className="rounded-full bg-foreground text-background h-8 px-3.5 text-[12.5px] font-medium inline-flex items-center gap-1">
          Get started <ArrowRight className="w-3 h-3"/>
        </button>
      </div>
      {/* Faux hero */}
      <div className="px-8 py-12 relative">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-foreground/15 bg-white/60 text-[10px] mono uppercase tracking-[0.18em]">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: LIME, boxShadow: `0 0 10px ${LIME}` }}/>
          {hero.eyebrow}
        </div>
        <h2 className="display font-semibold mt-4 text-3xl sm:text-4xl tracking-[-0.02em] leading-[1.05] whitespace-pre-line">
          {hero.title}
        </h2>
        <p className="mt-3 text-foreground/65 text-sm max-w-md leading-relaxed">{hero.sub}</p>
        <div className="mt-5 flex gap-2">
          <button className="rounded-full bg-foreground text-background h-9 px-4 text-[12.5px] font-medium inline-flex items-center gap-1">
            Apply for credit <ArrowRight className="w-3 h-3"/>
          </button>
          <button className="rounded-full border border-foreground/15 bg-white h-9 px-4 text-[12.5px] font-medium">
            See how it works
          </button>
        </div>
        {/* Faux accent burst */}
        <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full opacity-30 blur-3xl" style={{ background: LIME }}/>
      </div>
    </div>
  );
}

// ── Tagline pill ─────────────────────────────────────────────────────────
function TaglineRow({ text, idx }) {
  return (
    <div
      className="flex items-start gap-3 py-2.5"
      data-testid={`tagline-${idx}`}
    >
      <div className="w-5 h-5 rounded-full bg-foreground/5 grid place-items-center mt-0.5">
        <span className="text-[10px] mono text-foreground/70">{idx + 1}</span>
      </div>
      <div className="flex-1">
        <div className="display text-[18px] tracking-tight leading-tight">{text}</div>
      </div>
    </div>
  );
}

// ── Each candidate section ──────────────────────────────────────────────
function CandidateSection({ candidate, index }) {
  const { name, Mark, pronunciation, etymology, angle, taglines } = candidate;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="border-t border-foreground/10 first:border-0 py-14 sm:py-20"
      data-testid={`candidate-${name.toLowerCase()}`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left — identity */}
          <div className="lg:col-span-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/45">
              Candidate · 0{index + 1}
            </div>
            <div className="mt-5 flex items-center gap-4">
              <Mark size={64} />
              <div>
                <div className="display font-semibold text-5xl tracking-[-0.025em] leading-none">{name}</div>
                <div className="text-xs text-foreground/55 mono mt-2">/{pronunciation}/</div>
              </div>
            </div>
            <div className="mt-6 text-[13px] text-foreground/65 leading-relaxed">{etymology}</div>
            <div className="mt-2 text-[13px] text-foreground/85">{angle}</div>

            {/* Taglines */}
            <div className="mt-8 rounded-xl border border-foreground/10 bg-white/40 p-4">
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45 mb-1">
                Tagline options
              </div>
              {taglines.map((t, i) => <TaglineRow key={i} text={t} idx={i} />)}
            </div>

            {/* Mark sizing rail */}
            <div className="mt-8">
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45 mb-3">
                Across sizes
              </div>
              <div className="flex items-end gap-5">
                <Mark size={16} />
                <Mark size={24} />
                <Mark size={32} />
                <Mark size={48} />
                <Mark size={64} />
              </div>
            </div>

            {/* Inverse swatch */}
            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-foreground/10 bg-white p-4 flex items-center gap-3">
                <Mark size={28} />
                <div className="display font-semibold text-[15px]">{name}</div>
              </div>
              <div className="rounded-xl bg-foreground p-4 flex items-center gap-3 text-background">
                <Mark size={28} bg={LIME} fg={FOREST} />
                <div className="display font-semibold text-[15px]">{name}</div>
              </div>
            </div>
          </div>

          {/* Right — in-context hero mock */}
          <div className="lg:col-span-7">
            <HeroMock candidate={candidate} />
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function BrandPreview() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top strip — mirrors Landing.jsx nav */}
      <div className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-foreground/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-foreground grid place-items-center">
              <span className="text-accent font-bold text-[13px] display">T</span>
            </div>
            <span className="display font-semibold text-[15px] tracking-tight">TRAQO · Brand explorer</span>
          </div>
          <div className="text-[11.5px] mono uppercase tracking-[0.2em] text-foreground/55 hidden sm:block">
            3 candidates · in-context · pick a winner
          </div>
        </div>
      </div>

      {/* Hero intro */}
      <section className="max-w-6xl mx-auto px-6 pt-14 sm:pt-20 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-foreground/15 bg-white/60 backdrop-blur text-[10.5px] mono uppercase tracking-[0.2em]">
          <Sparkles className="w-3 h-3"/>
          Brand naming · round 1
        </div>
        <h1 className="display font-semibold mt-5 text-4xl sm:text-6xl tracking-[-0.03em] leading-[0.97] max-w-3xl">
          Pick the rebrand. <br/>
          <span className="text-foreground/45">Same DNA. Sharper name.</span>
        </h1>
        <p className="mt-5 text-foreground/65 max-w-xl leading-relaxed">
          Three candidate names rendered in the live TRAQO design language —
          cream · forest · electric lime, Bricolage Grotesque. Each one has a
          bespoke mark, three taglines, and a mock landing page so you can see
          how the brand feels in context.
        </p>
        <div className="mt-7 flex flex-wrap gap-2 text-[12.5px]">
          <a href="#verra" className="rounded-full border border-foreground/15 bg-white px-4 py-2 inline-flex items-center gap-2 hover:border-foreground/40 transition-colors" data-testid="jump-verra">
            <VerraMark size={18} /> Verra
          </a>
          <a href="#truva" className="rounded-full border border-foreground/15 bg-white px-4 py-2 inline-flex items-center gap-2 hover:border-foreground/40 transition-colors" data-testid="jump-truva">
            <TruvaMark size={18} /> Truva
          </a>
          <a href="#veris" className="rounded-full border border-foreground/15 bg-white px-4 py-2 inline-flex items-center gap-2 hover:border-foreground/40 transition-colors" data-testid="jump-veris">
            <VerisMark size={18} /> Veris
          </a>
        </div>
      </section>

      {/* Candidate sections */}
      <div className="mt-6">
        {CANDIDATES.map((c, i) => (
          <div id={c.name.toLowerCase()} key={c.name}>
            <CandidateSection candidate={c} index={i} />
          </div>
        ))}
      </div>

      {/* Side-by-side rail */}
      <section className="border-t border-foreground/10 py-16 bg-foreground text-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-background/55 mb-8">
            Side-by-side · final call
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {CANDIDATES.map((c) => {
              const { Mark, name, taglines } = c;
              return (
                <div key={name} className="rounded-2xl bg-background/5 border border-background/10 p-7">
                  <div className="flex items-center gap-3">
                    <Mark size={36} bg={LIME} fg={FOREST} />
                    <div className="display font-semibold text-2xl tracking-tight">{name}</div>
                  </div>
                  <div className="mt-5 text-[13px] text-background/75 leading-relaxed">{taglines[0]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brand-system footer (palette + type) */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/45 mb-6">
          Design DNA · unchanged
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-foreground/10 p-6">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45">
              Palette
            </div>
            <div className="mt-3 flex gap-3">
              <div className="flex-1 h-20 rounded-lg" style={{ background: CREAM, border: `1px solid ${FOREST}1a` }}>
                <div className="text-[10px] mono p-2">Cream<br/>#F7F5F0</div>
              </div>
              <div className="flex-1 h-20 rounded-lg" style={{ background: FOREST }}>
                <div className="text-[10px] mono p-2 text-background">Forest<br/>#0A1F1C</div>
              </div>
              <div className="flex-1 h-20 rounded-lg" style={{ background: LIME }}>
                <div className="text-[10px] mono p-2">Lime<br/>#E8FF3A</div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-foreground/10 p-6">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45">
              Type
            </div>
            <div className="mt-3">
              <div className="display font-semibold text-3xl tracking-[-0.02em]">Bricolage</div>
              <div className="text-[13px] text-foreground/65 mt-2">DM Sans · body</div>
              <div className="mono text-[12px] text-foreground/55 mt-1">JetBrains Mono · meta</div>
            </div>
          </div>

          <div className="rounded-2xl border border-foreground/10 p-6">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45">
              What changes
            </div>
            <ul className="mt-3 space-y-1.5 text-[13px] text-foreground/75">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-1 shrink-0"/> Logo + favicon</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-1 shrink-0"/> Wordmark across portal</li>
              <li className="flex items-start gap-2"><Zap className="w-3.5 h-3.5 mt-1 shrink-0"/> Email + PDF footers</li>
              <li className="flex items-start gap-2"><Layers className="w-3.5 h-3.5 mt-1 shrink-0"/> Domain → migration plan</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="border-t border-foreground/10 py-10">
        <div className="max-w-6xl mx-auto px-6 text-[12px] text-foreground/55 flex justify-between flex-wrap gap-3">
          <div>© 2026 — Brand explorer</div>
          <div className="mono">Pick one. We'll wire it everywhere in &lt; 1 hour.</div>
        </div>
      </footer>
    </div>
  );
}
